import { Component, output, ChangeDetectionStrategy } from "@angular/core";
import { SelectionModel } from "@angular/cdk/collections";
import { take } from "rxjs";
import { State } from "../../../models/state";
import { GeneralDataService } from "../../../services/GeneralDataService";

@Component({
  standalone: false,
  selector: 'app-state-filter',
  templateUrl: './state-filter.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./state-filter.component.scss']
})
export class StateFilterComponent {
  readonly onStateSelectionChange = output<State[] | undefined>();

  public states: State[] = [];

  /** Checkbox state while the menu is open (not yet applied). */
  public stateSelection = new SelectionModel<number>(true, []);

  /** Last applied selection — what menuLabel/allStatesSelected reflect, and what we revert to on outside-click close. */
  private committedStateIds: number[] = [];

  constructor(private gdService: GeneralDataService) {
    this.gdService.getStates().pipe(take(1)).subscribe(states => {
      this.states = states;
    });
  }

  public presetSelectedStates(sArray: State[] | undefined) {
    const ids = (sArray ?? []).map(s => s.id);
    this.committedStateIds = [...ids];
    this.stateSelection = new SelectionModel<number>(true, ids);
  }

  get allStatesSelected(): boolean {
    return this.committedStateIds.length === 0;
  }

  get menuLabel(): string {
    if (this.allStatesSelected) return "All States";
    const selected = this.states.filter(s => this.committedStateIds.includes(s.id));
    if (!selected.length) return "All States";
    const [first, ...rest] = selected;
    return first.name + (rest.length > 0 ? ` (+${rest.length})` : "");
  }

  public isStateChecked(s: State): boolean {
    return this.stateSelection.isSelected(s.id);
  }

  public toggleAllStates(checked: boolean) {
    if (checked) this.stateSelection.clear();
  }

  public toggleState(s: State) {
    this.stateSelection.toggle(s.id);
  }

  /** Fires with the reason the menu closed: falsy = dismissed via outside click -> discard staged changes. */
  public onMenuClosed(reason: any) {
    if (!reason) {
      this.stateSelection = new SelectionModel<number>(true, this.committedStateIds);
    }
  }

  public onApplyClick() {
    this.committedStateIds = [...this.stateSelection.selected];
    this.emitSelection();
  }

  public onClearClick() {
    this.stateSelection.clear();
    this.committedStateIds = [];
    this.emitSelection();
  }

  private emitSelection() {
    if (this.allStatesSelected) {
      this.onStateSelectionChange.emit(undefined);
      return;
    }

    const sArray = this.states.filter(s => this.stateSelection.isSelected(s.id));
    this.onStateSelectionChange.emit(sArray);
  }
}